import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useState } from 'react';
import Sidebar from './components/Sidebar';
import MainContent from './components/MainContent';
import VisionValues from './components/VisionValues';
import Footer from './components/Footer';
import About from './components/About';
import Services from './components/Services';
import Contact from './components/Contact';
import PPEs from './components/PPEs';
import FireExtinguishers from './components/services/FireExtinguishers';
import FireAlarmDetection from './components/services/FireAlarmDetection';
import SprinklerSystems from './components/services/SprinklerSystems';
import EmergencyLighting from './components/services/EmergencyLighting';
import FireSafetyTraining from './components/services/FireSafetyTraining';
import ComplianceInspection from './components/services/ComplianceInspection';
import OxygenCylinders from './components/services/OxygenCylinders';
import Fabrication from './components/services/Fabrication';
import FireHydrantSystems from './components/services/FireHydrantSystems';
import WaterModularTank from './components/services/WaterModularTank';
import HeadProtection from './components/ppe/HeadProtection';
import EyeFaceProtection from './components/ppe/EyeFaceProtection';
import HearingProtection from './components/ppe/HearingProtection';
import RespiratoryProtection from './components/ppe/RespiratoryProtection';
import HandProtection from './components/ppe/HandProtection';
import BodyProtection from './components/ppe/BodyProtection';
import FootProtection from './components/ppe/FootProtection';
import Brands from './components/brands/Brands';
import BrandDetail from './components/brands/BrandDetail';
import BrandCategory from './components/brands/BrandCategory';
import { ThemeProvider } from './components/ThemeContext';
import { useCanonicalUrl } from './hooks/useCanonicalUrl';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useCanonicalUrl();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const RouteTransition = ({ children }) => {
  const prefersReduced = useReducedMotion();

  return (
    <motion.div
      initial={prefersReduced ? false : { opacity: 0, y: 10 }}
      animate={prefersReduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
      exit={prefersReduced ? { opacity: 1 } : { opacity: 0, y: -8 }}
      transition={{ duration: prefersReduced ? 0 : 0.3, ease: 'easeOut' }}
      className="h-full"
    >
      {children}
    </motion.div>
  );
};

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <RouteTransition>
              <>
                <MainContent />
                <VisionValues />
              </>
            </RouteTransition>
          }
        />

        <Route path="/about" element={<RouteTransition><About /></RouteTransition>} />

        <Route path="/services" element={<RouteTransition><Services /></RouteTransition>} />
        <Route path="/services/fire-extinguishers" element={<RouteTransition><FireExtinguishers /></RouteTransition>} />
        <Route path="/services/fire-alarm-detection" element={<RouteTransition><FireAlarmDetection /></RouteTransition>} />
        <Route path="/services/smoke-detection" element={<RouteTransition><FireAlarmDetection /></RouteTransition>} />
        <Route path="/services/sprinkler-systems" element={<RouteTransition><SprinklerSystems /></RouteTransition>} />
        <Route path="/services/fire-hydrant-systems" element={<RouteTransition><FireHydrantSystems /></RouteTransition>} />
        <Route path="/services/water-modular-tank" element={<RouteTransition><WaterModularTank /></RouteTransition>} />
        <Route path="/services/emergency-lighting" element={<RouteTransition><EmergencyLighting /></RouteTransition>} />
        <Route path="/services/fire-safety-training" element={<RouteTransition><FireSafetyTraining /></RouteTransition>} />
        <Route path="/services/compliance-inspection" element={<RouteTransition><ComplianceInspection /></RouteTransition>} />
        <Route path="/services/oxygen-cylinders" element={<RouteTransition><OxygenCylinders /></RouteTransition>} />
        <Route path="/services/fabrication" element={<RouteTransition><Fabrication /></RouteTransition>} />

        <Route path="/services/ppe" element={<RouteTransition><PPEs /></RouteTransition>} />
        <Route path="/services/ppe/head-protection" element={<RouteTransition><HeadProtection /></RouteTransition>} />
        <Route path="/services/ppe/eye-face-protection" element={<RouteTransition><EyeFaceProtection /></RouteTransition>} />
        <Route path="/services/ppe/hearing-protection" element={<RouteTransition><HearingProtection /></RouteTransition>} />
        <Route path="/services/ppe/respiratory-protection" element={<RouteTransition><RespiratoryProtection /></RouteTransition>} />
        <Route path="/services/ppe/hand-protection" element={<RouteTransition><HandProtection /></RouteTransition>} />
        <Route path="/services/ppe/body-protection" element={<RouteTransition><BodyProtection /></RouteTransition>} />
        <Route path="/services/ppe/foot-protection" element={<RouteTransition><FootProtection /></RouteTransition>} />

        <Route path="/ppe" element={<RouteTransition><PPEs /></RouteTransition>} />
        <Route path="/ppe/head-protection" element={<RouteTransition><HeadProtection /></RouteTransition>} />
        <Route path="/ppe/eye-face-protection" element={<RouteTransition><EyeFaceProtection /></RouteTransition>} />
        <Route path="/ppe/hearing-protection" element={<RouteTransition><HearingProtection /></RouteTransition>} />
        <Route path="/ppe/respiratory-protection" element={<RouteTransition><RespiratoryProtection /></RouteTransition>} />
        <Route path="/ppe/hand-protection" element={<RouteTransition><HandProtection /></RouteTransition>} />
        <Route path="/ppe/body-protection" element={<RouteTransition><BodyProtection /></RouteTransition>} />
        <Route path="/ppe/foot-protection" element={<RouteTransition><FootProtection /></RouteTransition>} />

        <Route path="/contact" element={<RouteTransition><Contact /></RouteTransition>} />
        <Route path="/brands" element={<RouteTransition><Brands /></RouteTransition>} />
        <Route path="/brands/:brandName" element={<RouteTransition><BrandDetail /></RouteTransition>} />
        <Route path="/brands/:brandName/:categoryName" element={<RouteTransition><BrandCategory /></RouteTransition>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  return (
    <Router>
      <ScrollToTop />
      <ThemeProvider>
        <div className="App relative min-h-screen bg-[#111111] text-[#F8F9FA] transition-colors duration-300">
          <div className="pattern-bg" aria-hidden="true" />
          <Sidebar isCollapsed={isSidebarCollapsed} setIsCollapsed={setIsSidebarCollapsed} />
          <div className={`relative z-10 min-h-screen transition-all duration-300 ${isSidebarCollapsed ? 'md:pl-24' : 'md:pl-72'}`}>
            <main className="min-h-[calc(100vh-220px)] pt-16 md:pt-0">
              <AnimatedRoutes />
            </main>
            <Footer />
          </div>
        </div>
      </ThemeProvider>
    </Router>
  );
}

export default App;
